<template>
  <div class="edit-job-page">
    <div class="page-header">
      <h1 class="page-title">공고 수정</h1>
      <p class="page-subtitle">공고 정보를 수정해주세요</p>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else-if="!job" class="error-state">
      <p>공고를 찾을 수 없습니다.</p>
      <router-link to="/mypage/jobs" class="btn-primary">내 공고로 돌아가기</router-link>
    </div>

    <form v-else @submit.prevent="handleSubmit" class="job-form">
      <section class="form-section">
        <h2 class="section-title">기본 정보</h2>
        
        <div class="input-group">
          <label for="title">공고 제목 <span class="required">*</span></label>
          <input type="text" id="title" v-model="form.title" required />
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="siteName">현장명 <span class="required">*</span></label>
            <input type="text" id="siteName" v-model="form.siteName" required />
          </div>
          <div class="input-group">
            <label for="startDate">투입일 <span class="required">*</span></label>
            <input type="date" id="startDate" v-model="form.startDate" required />
          </div>
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="deadline">마감일</label>
            <input 
              type="date" 
              id="deadline" 
              v-model="form.deadline"
              placeholder="모집 마감일"
            />
          </div>
          <div class="input-group">
            <label>&nbsp;</label>
            <div class="deadline-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>마감일을 설정하면 D-Day가 표시됩니다</span>
            </div>
          </div>
        </div>

        <div class="input-group">
          <label for="address">현장 주소 <span class="required">*</span></label>
          <div class="address-search-wrapper">
            <div class="address-input-box">
              <input 
                type="text" 
                id="address" 
                v-model="form.address" 
                placeholder="주소 검색을 클릭하여 주소를 입력해주세요"
                readonly
                @click="openAddressSearch"
                required
              />
              <button type="button" class="btn-search" @click="openAddressSearch">
                주소 검색
              </button>
            </div>
            <input 
              type="text" 
              v-model="form.detailAddress" 
              placeholder="상세 주소 (선택사항)"
              class="detail-address-input"
            />
          </div>

          <div v-if="form.address" class="map-preview-area">
            <KakaoMap 
              :address="form.address" 
              :markerTitle="form.siteName || '현장 위치'"
              height="300px"
            />
          </div>
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="region">지역 <span class="required">*</span></label>
            <select id="region" v-model="form.region" required>
              <option value="">선택하세요</option>
              <option v-for="region in regions" :key="region" :value="region">{{ region }}</option>
            </select>
          </div>
          <div class="input-group">
            <label for="industry">업종 <span class="required">*</span></label>
            <select id="industry" v-model="form.industry" required>
              <option value="">선택하세요</option>
              <option v-for="industry in industries" :key="industry" :value="industry">{{ industry }}</option>
            </select>
          </div>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">회사 정보</h2>
        
        <div class="input-row">
          <div class="input-group">
            <label for="constructor">시공사</label>
            <input type="text" id="constructor" v-model="form.constructor" />
          </div>
          <div class="input-group">
            <label for="developer">시행사</label>
            <input type="text" id="developer" v-model="form.developer" />
          </div>
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="agency">대행사</label>
            <input type="text" id="agency" v-model="form.agency" />
          </div>
          <div class="input-group">
            <label for="trust">신탁사</label>
            <input type="text" id="trust" v-model="form.trust" />
          </div>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">모집 정보</h2>
        
        <div class="input-row">
          <div class="input-group">
            <label for="jobType">직종 <span class="required">*</span></label>
            <select id="jobType" v-model="form.jobType" required>
              <option value="">선택하세요</option>
              <option v-for="type in jobTypes" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
          <div class="input-group">
            <label for="headcount">모집인원</label>
            <input type="text" id="headcount" v-model="form.headcount" />
          </div>
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="gender">성별</label>
            <select id="gender" v-model="form.gender">
              <option v-for="gender in genders" :key="gender" :value="gender">{{ gender }}</option>
            </select>
          </div>
          <div class="input-group">
            <label for="age">나이</label>
            <input type="text" id="age" v-model="form.age" />
          </div>
        </div>

        <div class="input-group">
          <label for="experience">경력</label>
          <select id="experience" v-model="form.experience">
            <option v-for="exp in experiences" :key="exp" :value="exp">{{ exp }}</option>
          </select>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">급여 정보</h2>
        
        <div class="input-row">
          <div class="input-group">
            <label for="salaryType">급여 형태 <span class="required">*</span></label>
            <select id="salaryType" v-model="form.salaryType" required>
              <option value="">선택하세요</option>
              <option v-for="type in salaryTypes" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
          <div class="input-group">
            <label for="salary">급여 <span class="required">*</span></label>
            <input type="text" id="salary" v-model="form.salary" required />
          </div>
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="transportationFee">교통비</label>
            <input type="text" id="transportationFee" v-model="form.transportationFee" />
          </div>
          <div class="input-group">
            <label for="salesFee">영업비</label>
            <input type="text" id="salesFee" v-model="form.salesFee" />
          </div>
        </div>

        <div class="input-group">
          <label for="benefits">근무 후생</label>
          <input type="text" id="benefits" v-model="form.benefits" />
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">현장 이미지</h2>
        
        <div class="image-upload">
          <input 
            type="file" 
            id="thumbnail" 
            ref="fileInput"
            accept="image/*"
            @change="handleImageChange"
            hidden
          />
          
          <div v-if="!imagePreview" class="upload-placeholder" @click="$refs.fileInput.click()">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#adb5bd" stroke-width="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <p>클릭하여 이미지 변경</p>
            <span>권장 사이즈: 1920 x 1080 (16:9)</span>
          </div>
          
          <div v-else class="image-preview">
            <img :src="imagePreview" alt="미리보기" />
            <button type="button" class="change-btn" @click="$refs.fileInput.click()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              이미지 변경
            </button>
          </div>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">상세 내용</h2>
        
        <div class="input-group">
          <label for="description">상세 설명</label>
          <textarea id="description" v-model="form.description" rows="8"></textarea>
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">담당자 정보</h2>
        
        <div class="input-row">
          <div class="input-group">
            <label for="contactName">담당자명 <span class="required">*</span></label>
            <input type="text" id="contactName" v-model="form.contactName" required />
          </div>
          <div class="input-group">
            <label for="contactPhone">연락처 <span class="required">*</span></label>
            <input type="tel" id="contactPhone" v-model="form.contactPhone" required />
          </div>
        </div>

        <div class="input-row">
          <div class="input-group">
            <label for="siteUrl">사이트 주소</label>
            <input type="url" id="siteUrl" v-model="form.siteUrl" />
          </div>
          <div class="input-group">
            <label for="snsUrl">SNS 링크</label>
            <input type="url" id="snsUrl" v-model="form.snsUrl" />
          </div>
        </div>
      </section>

      <p v-if="submitError" class="error-message">{{ submitError }}</p>

      <div class="form-actions">
        <button type="button" class="btn-secondary" @click="$router.back()">취소</button>
        <button type="submit" class="btn-primary" :disabled="isSubmitting">
          {{ isSubmitting ? '저장 중...' : '수정 완료' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useJobs, JOB_CONSTANTS } from '@/composables/useJobs'
import KakaoMap from '@/components/KakaoMap.vue'

export default {
  name: 'EditJobView',
  components: {
    KakaoMap
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { user } = useAuth()
    const { getJob, updateJob, uploadImage } = useJobs()

    const job = ref(null)
    const loading = ref(true)
    const fileInput = ref(null)
    const imageFile = ref(null)
    const imagePreview = ref(null)
    const isSubmitting = ref(false)
    const submitError = ref(null)

    const form = reactive({
      title: '',
      siteName: '',
      startDate: '',
      deadline: '', // 마감일 추가
      address: '',
      detailAddress: '',
      region: '',
      industry: '',
      constructor: '',
      developer: '',
      agency: '',
      trust: '',
      jobType: '',
      headcount: '',
      gender: '무관',
      age: '',
      experience: '무관',
      salaryType: '',
      salary: '',
      transportationFee: '',
      salesFee: '',
      benefits: '',
      description: '',
      contactName: '',
      contactPhone: '',
      siteUrl: '',
      snsUrl: '',
      thumbnail: ''
    })

    const { regions, industries, jobTypes, salaryTypes, genders, experiences } = JOB_CONSTANTS

    const loadPostcodeScript = () => {
      if (document.getElementById('daum-postcode-script')) return
      
      const script = document.createElement('script')
      script.id = 'daum-postcode-script'
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
      document.head.appendChild(script)
    }

    const openAddressSearch = () => {
      if (!window.daum || !window.daum.Postcode) {
        alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.')
        return
      }

      new window.daum.Postcode({
        oncomplete: (data) => {
          let addr = ''
          if (data.userSelectedType === 'R') {
            addr = data.roadAddress
          } else {
            addr = data.jibunAddress
          }
          form.address = addr
        }
      }).open()
    }

    const loadJob = async () => {
      loading.value = true
      try {
        const jobId = route.params.id
        const jobData = await getJob(jobId)
        
        if (!jobData) {
          loading.value = false
          return
        }

        if (jobData.userId !== user.value?.uid) {
          router.push('/mypage/jobs')
          return
        }

        job.value = jobData
        
        Object.keys(form).forEach(key => {
          if (jobData[key] !== undefined) {
            form[key] = jobData[key]
          }
        })
        
        if (jobData.thumbnail) {
          imagePreview.value = jobData.thumbnail
        }
      } catch (e) {
        console.error('공고 로드 실패:', e)
      } finally {
        loading.value = false
      }
    }

    const handleImageChange = (e) => {
      const file = e.target.files[0]
      if (file) {
        imageFile.value = file
        imagePreview.value = URL.createObjectURL(file)
      }
    }

    const handleSubmit = async () => {
      isSubmitting.value = true
      submitError.value = null

      try {
        if (imageFile.value) {
          form.thumbnail = await uploadImage(imageFile.value)
        }
        
        await updateJob(job.value.id, form)
        router.push(`/jobs/${job.value.id}`)
      } catch (e) {
        console.error('공고 수정 실패:', e)
        submitError.value = '공고 수정에 실패했습니다. 다시 시도해주세요.'
      } finally {
        isSubmitting.value = false
      }
    }

    onMounted(() => {
      loadPostcodeScript()
      loadJob()
    })

    return {
      job,
      form,
      loading,
      regions,
      industries,
      jobTypes,
      salaryTypes,
      genders,
      experiences,
      fileInput,
      imagePreview,
      isSubmitting,
      submitError,
      handleImageChange,
      handleSubmit,
      openAddressSearch
    }
  }
}
</script>

<style scoped>
.edit-job-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}

.page-header {
  margin-bottom: 40px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 15px;
  color: #6b7684;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.job-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-section {
  background: #fff;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f2f4f6;
}

.input-group {
  margin-bottom: 20px;
}

.input-group:last-child {
  margin-bottom: 0;
}

.input-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.required {
  color: #e03131;
}

.input-group input,
.input-group select,
.input-group textarea {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.15s;
  background: #fff;
}

.input-group input:focus,
.input-group select:focus,
.input-group textarea:focus {
  outline: none;
  border-color: #3182f6;
  box-shadow: 0 0 0 3px rgba(49, 130, 246, 0.1);
}

.input-group textarea {
  resize: vertical;
  min-height: 120px;
}

.input-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* 마감일 힌트 */
.deadline-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: #f0f7ff;
  border-radius: 10px;
  height: 50px;
}

.deadline-hint svg {
  color: #3182f6;
  flex-shrink: 0;
}

.deadline-hint span {
  font-size: 13px;
  color: #1971c2;
  font-weight: 500;
}

/* 주소 검색 */
.address-search-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.address-input-box {
  display: flex;
  gap: 8px;
}

.address-input-box input {
  flex: 1;
  cursor: pointer;
}

.btn-search {
  padding: 0 16px;
  height: 50px;
  background: #333;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}

.btn-search:hover {
  background: #191f28;
}

.detail-address-input {
  margin-top: 4px;
}

.map-preview-area {
  margin-top: 16px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e8eb;
}

/* 이미지 업로드 */
.image-upload {
  margin-top: 8px;
}

.upload-placeholder {
  border: 2px dashed #e5e8eb;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
}

.upload-placeholder:hover {
  border-color: #3182f6;
  background: #f9fafb;
}

.upload-placeholder p {
  font-size: 15px;
  font-weight: 500;
  color: #4e5968;
  margin: 16px 0 4px;
}

.upload-placeholder span {
  font-size: 13px;
  color: #8b95a1;
}

.image-preview {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.image-preview img {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: contain;
  background: #f2f4f6;
}

.change-btn {
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.change-btn:hover {
  background: rgba(0, 0, 0, 0.85);
}

/* 에러 메시지 */
.error-message {
  color: #e03131;
  font-size: 14px;
  text-align: center;
  padding: 16px;
  background: #fff5f5;
  border-radius: 8px;
}

/* 버튼 */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-primary {
  padding: 16px 32px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: #1b64da;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 16px 32px;
  background: #f2f4f6;
  color: #333;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover {
  background: #e5e8eb;
}

@media (max-width: 768px) {
  .edit-job-page {
    padding: 24px 16px 60px;
  }

  .page-title {
    font-size: 24px;
  }

  .form-section {
    padding: 24px;
  }

  .input-row {
    grid-template-columns: 1fr;
  }

  .deadline-hint {
    margin-top: 8px;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>